import React, { useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const toKey = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

const formatLabel = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return `${d} ${MONTH_NAMES[m - 1].slice(0, 3)} ${y}`;
};

// Pick specific calendar dates (not a recurring weekly pattern) — used to mark a
// doctor unavailable on individual days (leave/holiday) on top of their weekly schedule.
const DateMultiPicker = ({ selectedDates = [], onChange }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const todayKey = toKey(today.getFullYear(), today.getMonth(), today.getDate());
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const toggleDate = (key) => {
    if (key < todayKey) return;
    if (selectedDates.includes(key)) {
      onChange(selectedDates.filter((d) => d !== key));
    } else {
      onChange([...selectedDates, key].sort());
    }
  };

  const changeMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setViewMonth(m);
    setViewYear(y);
  };

  const cells = [];
  for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', maxWidth: '320px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <button type="button" className="btn btn-secondary btn-icon" onClick={() => changeMonth(-1)}>
            <FiChevronLeft />
          </button>
          <strong style={{ fontSize: '14px' }}>{MONTH_NAMES[viewMonth]} {viewYear}</strong>
          <button type="button" className="btn btn-secondary btn-icon" onClick={() => changeMonth(1)}>
            <FiChevronRight />
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '4px' }}>
          {WEEKDAYS.map((w) => (
            <div key={w} style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', fontWeight: 600 }}>
              {w}
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
          {cells.map((d, i) => {
            if (d === null) return <div key={`empty-${i}`} />;
            const key = toKey(viewYear, viewMonth, d);
            const isPast = key < todayKey;
            const isToday = key === todayKey;
            const isSelected = selectedDates.includes(key);
            return (
              <button
                type="button"
                key={key}
                onClick={() => toggleDate(key)}
                disabled={isPast}
                title={isSelected ? 'Marked unavailable — click to remove' : 'Click to mark unavailable'}
                style={{
                  width: '100%',
                  aspectRatio: '1',
                  borderRadius: '6px',
                  border: isToday ? '1px solid #4f46e5' : '1px solid transparent',
                  background: isSelected ? '#ef4444' : isPast ? 'transparent' : '#f9fafb',
                  color: isSelected ? '#fff' : isPast ? '#d1d5db' : '#374151',
                  cursor: isPast ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: isToday ? 700 : 400,
                }}
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>

      {selectedDates.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
          {[...selectedDates].sort().map((key) => (
            <span
              key={key}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                background: '#fef2f2',
                color: '#b91c1c',
                border: '1px solid #fecaca',
                borderRadius: '999px',
                padding: '3px 8px 3px 10px',
                fontSize: '12px',
              }}
            >
              {formatLabel(key)}
              <FiX
                style={{ cursor: 'pointer' }}
                onClick={() => onChange(selectedDates.filter((d) => d !== key))}
              />
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default DateMultiPicker;
